import { Email } from '@/components/common/text_input/Email';
import { ApplicationStatusEmailRequestDto } from '@/models/Admin/Emails/EmailRequestDtos';
import { ApplicationTypes } from '@/models/ApplicationType';
import { programIdToProgramName, ProgramNames } from '@/models/Program';
import { ApplicationStatus, ApplicationStatusesText } from '@/utils/constants';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import React from 'react';

type Props = {
  form: ApplicationStatusEmailRequestDto;
  setForm: React.Dispatch<React.SetStateAction<ApplicationStatusEmailRequestDto>>;
  programOptions: ProgramNames[];
  applicationStatusOptions: ApplicationStatus[];
  applicationTypeOptions: ApplicationTypes[];
  localeOptions: ReadonlyArray<{ value: string; label: string }>;
  defaultLocale: string;
};

const ApplicationStatusForm: React.FC<Props> = ({
  form,
  setForm,
  programOptions,
  applicationStatusOptions,
  applicationTypeOptions,
  localeOptions,
  defaultLocale,
}) => {
  const showRevisionStatus = form.NewStatus === ApplicationStatus.InRevision;
  const isRevisionStatusMissing = showRevisionStatus && form.RevisionStatus == null;

  return (
    <Stack spacing={2}>
      <Email
        fullWidth
        required
        label='Email'
        value={form.Email}
        onChange={(e) => setForm((prev) => ({ ...prev, Email: e.target.value }))}
      />
      <TextField
        fullWidth
        required
        type='number'
        label='Application Id'
        value={form.ApplicationId}
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            ApplicationId: Number(e.target.value),
          }))
        }
      />
      <FormControl fullWidth>
        <InputLabel id='app-status-program-label' required>
          Program
        </InputLabel>
        <Select
          labelId='app-status-program-label'
          value={String(form.Program)}
          label='Program'
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              Program: Number(e.target.value),
            }))
          }
        >
          {programOptions.map((programId) => (
            <MenuItem key={programId} value={String(programId)}>
              {programIdToProgramName[programId]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        fullWidth
        required
        label='Partner Name'
        value={form.PartnerName}
        onChange={(e) => setForm((prev) => ({ ...prev, PartnerName: e.target.value }))}
      />
      <TextField
        fullWidth
        required
        type='number'
        label='Recognition Year'
        value={form.RecognitionYear}
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            RecognitionYear: Number(e.target.value),
          }))
        }
      />
      <FormControl fullWidth>
        <InputLabel id='application-status-locale-code-label' required>
          Locale Code
        </InputLabel>
        <Select
          labelId='application-status-locale-code-label'
          value={form.LocaleCode ?? defaultLocale}
          label='Locale Code'
          onChange={(e) => setForm((prev) => ({ ...prev, LocaleCode: e.target.value }))}
        >
          {localeOptions.map((localeOption) => (
            <MenuItem key={localeOption.value} value={localeOption.value}>
              {localeOption.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth>
        <InputLabel id='new-status-label' required>
          New Status
        </InputLabel>
        <Select
          labelId='new-status-label'
          value={String(form.NewStatus)}
          label='New Status'
          onChange={(e) =>
            setForm((prev) => {
              const newStatus = Number(e.target.value);
              const isInRevision = newStatus === Number(ApplicationStatus.InRevision);

              return {
                ...prev,
                NewStatus: newStatus,
                RevisionStatus: isInRevision ? prev.RevisionStatus : null,
              };
            })
          }
        >
          {applicationStatusOptions.map((status) => (
            <MenuItem key={status} value={String(status)}>
              {ApplicationStatusesText[status]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {showRevisionStatus && (
        <FormControl fullWidth required error={isRevisionStatusMissing}>
          <InputLabel id='revision-status-label' required sx={{ pointerEvents: 'auto' }}>
            <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              Revision Status
              <Tooltip title='Set this to the status that the application was sent back to the user from.'>
                <InfoOutlinedIcon fontSize='small' />
              </Tooltip>
            </Box>
          </InputLabel>
          <Select
            labelId='revision-status-label'
            value={form.RevisionStatus == null ? '' : String(form.RevisionStatus)}
            label='Revision Status'
            required
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                RevisionStatus: e.target.value === '' ? null : Number(e.target.value),
              }))
            }
          >
            <MenuItem value=''>None</MenuItem>
            {applicationStatusOptions.map((status) => (
              <MenuItem key={status} value={String(status)}>
                {ApplicationStatusesText[status]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <FormControl fullWidth>
        <InputLabel id='application-type-label' required>
          Application Type
        </InputLabel>
        <Select
          labelId='application-type-label'
          value={String(form.ApplicationType)}
          label='Application Type'
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              ApplicationType: Number(e.target.value),
            }))
          }
        >
          {applicationTypeOptions.map((applicationType) => (
            <MenuItem key={applicationType} value={String(applicationType)}>
              {ApplicationTypes[applicationType]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControlLabel
        control={
          <Checkbox
            checked={form.HasStateCoordinator}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                HasStateCoordinator: e.target.checked,
              }))
            }
          />
        }
        label='Has State Coordinator'
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={form.HasRegionalCoordinator}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                HasRegionalCoordinator: e.target.checked,
              }))
            }
          />
        }
        label='Has Regional Coordinator'
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={form.HasNationalProgrammeLead}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                HasNationalProgrammeLead: e.target.checked,
              }))
            }
          />
        }
        label='Has National Programme Lead'
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={form.IsResubmitted}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                IsResubmitted: e.target.checked,
              }))
            }
          />
        }
        label='Is Resubmitted'
      />
    </Stack>
  );
};

export default ApplicationStatusForm;
